import React, { ComponentType } from 'react';

import { useAuth } from '../../contexts/AuthContext';

interface WithAuthOptions {
  /**
   * 未認証時に表示するフォールバックコンポーネント
   */
  fallback?: React.ComponentType;
  /**
   * 認証チェック中に表示するローディングコンポーネント
   */
  loadingComponent?: React.ComponentType;
  /**
   * 認証が必要かどうかを判定する関数
   * デフォルトは常にtrueを返す（認証必須）
   */
  requireAuth?: boolean;
  /**
   * 認証状態に基づいて条件付きレンダリングを行うかどうか
   * trueの場合、未認証時はnullを返す（何も表示しない）
   */
  conditionalRender?: boolean;
}

/**
 * 認証ガード高次コンポーネント
 *
 * 要件 6.1: ログイン済みユーザーが記事詳細画面で「コメントする」ボタンを押下したとき、システムはコメント投稿フォームを表示する
 * 要件 6.2: 非ログインユーザーが記事詳細画面で「コメントする」ボタンを押下したとき、システムは登録画面に誘導する
 */
const withAuth = <P extends object>(
  WrappedComponent: ComponentType<P>,
  options: WithAuthOptions = {}
) => {
  const {
    fallback: FallbackComponent,
    loadingComponent: LoadingComponent,
    requireAuth = true,
    conditionalRender = false,
  } = options;

  const AuthenticatedComponent: React.FC<P> = props => {
    const { isAuthenticated, isLoading } = useAuth();

    // 認証状態確認中の場合
    if (isLoading) {
      if (LoadingComponent) {
        return <LoadingComponent />;
      }

      return (
        <div className="flex items-center justify-center p-4">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
            data-testid="default-loading"
          ></div>
          <span className="ml-2 text-gray-600">認証状態を確認中...</span>
        </div>
      );
    }

    // 認証が不要な場合は常に表示
    if (!requireAuth) {
      return <WrappedComponent {...props} />;
    }

    // 認証が必要だが未認証の場合
    if (!isAuthenticated) {
      // 条件付きレンダリングの場合は何も表示しない
      if (conditionalRender) {
        return null;
      }

      // フォールバックコンポーネントがある場合はそれを表示
      if (FallbackComponent) {
        return <FallbackComponent />;
      }

      // デフォルトのログイン誘導メッセージ
      return (
        <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-blue-800 mb-2">この機能を利用するにはログインが必要です</p>
          <button
            onClick={() => {
              // ログインモーダルを開く処理（実装は別途必要）
              // eslint-disable-next-line no-console
              console.log('Open login modal');
            }}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            ログイン
          </button>
        </div>
      );
    }

    // 認証済みの場合は元のコンポーネントを表示
    return <WrappedComponent {...props} />;
  };

  // デバッグ用にコンポーネント名を設定
  AuthenticatedComponent.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name})`;

  return AuthenticatedComponent;
};

export default withAuth;
